import { useInfiniteQuery } from '@tanstack/react-query';
import { getV1MessagingChannelsMessagingChannelIdMessagesIdReaders } from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { useMemo } from 'react';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { MessagingChannelMember } from '~hooks/useMessaging/models';
import { messagingMessageReadersQueryKey } from '~queries/messagingQueryKeys';
import { extractPagination } from '~services/pagination';
import reportRequestError from '~services/reportRequestError';

type MessagingMessageReadersPage = {
  nextCursor: string | undefined;
  readers: MessagingChannelMember[];
};

export default function useMessagingMessageReadersInfiniteQuery({
  channelId,
  messageId,
}: {
  channelId: string;
  messageId: string;
}) {
  const query = useInfiniteQuery({
    queryKey: messagingMessageReadersQueryKey(channelId, messageId),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }): Promise<MessagingMessageReadersPage> => {
      try {
        const response = await getV1MessagingChannelsMessagingChannelIdMessagesIdReaders({
          path: { 'messaging-channel-id': channelId, id: messageId },
          query: {
            'page[limit]': DEFAULT_API_PAGE_SIZE,
            ...(pageParam ? { 'page[after]': pageParam } : {}),
          },
          signal,
        });

        return {
          nextCursor: extractPagination(response).after,
          readers: response.data.data.map(MessagingChannelMember.parse),
        };
      } catch (error) {
        // React Query drops a read it cancelled, so there is nothing to report.
        if (axios.isCancel(error)) throw error;

        reportRequestError(error, t('error_messages.messaging.failed_to_fetch_readers'));

        throw error;
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    retry: false,
  });

  const readers = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.readers),
    [query.data?.pages],
  );

  return {
    fetchMore: query.fetchNextPage,
    hasMore: query.hasNextPage,
    isError: query.isError,
    isLoading: query.isLoading,
    isLoadingMore: query.isFetchingNextPage,
    readers,
  };
}
